import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CommissionReportPage.css";
import {
  COLORS,
  Chip,
  DonutChart,
  BarList,
  ChartLegend,
  PaginationBar,
  PersonCell,
  asNum,
  formatAmount,
  formatDate,
  formatPercent,
  formatCurrencyLabel,
  personName,
  accountClass,
  sourceLabel,
  stakingStatusKind,
  dateToFromIso,
  dateToToIso,
} from "./commissionReportShared";

const EMPTY_PAGINATION = { page: 1, limit: 20, total: 0, total_pages: 1, has_next: false, has_prev: false };
const TAB_IDS = ["overview", "staking", "trades", "downline", "upline"];

const sourceChipKind = (src) => {
  const s = String(src || "").toUpperCase();
  if (s.includes("TRADE") || s.includes("BROKER")) return "brokerage";
  return "staking";
};

const PreviewHead = ({ title, count, onViewAll, disabled }) => (
  <div className="cr-preview-head">
    <h3>
      {title}
      {count != null ? <span className="cr-muted"> · {count} total</span> : null}
    </h3>
    {onViewAll ? (
      <button type="button" className="cr-link-btn" onClick={onViewAll} disabled={disabled}>
        View all {count != null ? count : ""} →
      </button>
    ) : null}
  </div>
);

const EmptyBlock = ({ loading, error, empty }) => {
  if (loading) {
    return (
      <div className="cr-empty">
        <i className="fas fa-spinner fa-spin" />
        <p>Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="cr-empty">
        <i className="fas fa-exclamation-circle" />
        <p>{error}</p>
      </div>
    );
  }
  return (
    <div className="cr-empty">
      <i className="fas fa-inbox" />
      <p>{empty || "No records."}</p>
    </div>
  );
};

const DateTypeFilters = ({ fromDate, toDate, typeValue, onFrom, onTo, onType, onApply, onReset, showType }) => (
  <form
    className="cr-filters"
    onSubmit={(e) => {
      e.preventDefault();
      onApply();
    }}
    style={{ marginBottom: "0.85rem" }}
  >
    <label>
      From
      <input type="date" value={fromDate} onChange={(e) => onFrom(e.target.value)} />
    </label>
    <label>
      To
      <input type="date" value={toDate} onChange={(e) => onTo(e.target.value)} />
    </label>
    {showType ? (
      <label>
        Type
        <select value={typeValue} onChange={(e) => onType(e.target.value)}>
          <option value="">Staking + trading</option>
          <option value="staking">Staking</option>
          <option value="brokerage">Trading</option>
        </select>
      </label>
    ) : null}
    <button type="submit" className="cr-btn-primary">
      Apply
    </button>
    <button type="button" className="cr-btn-ghost" onClick={onReset}>
      Reset
    </button>
  </form>
);

const StakingTable = ({ rows, expanded, onToggle, compact }) => {
  if (!rows.length) return <p className="cr-muted">No staking packages.</p>;
  return (
    <div className="cr-table-wrap">
      <table className="cr-table">
        <thead>
          <tr>
            <th>Package</th>
            <th>Amount</th>
            <th>Dates</th>
            <th>Status</th>
            <th>Paid / expected</th>
            <th>Upline commission</th>
            {!compact ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => {
            const pkg = item?.package || {};
            const gen = item?.commission_generated_for_uplines || {};
            const id = pkg._id || `stake-${idx}`;
            const open = expanded.has(id);
            const events = Array.isArray(gen.payout_wise) ? gen.payout_wise : [];
            return (
              <React.Fragment key={id}>
                <tr>
                  <td>
                    <strong>
                      {pkg.package_currency || pkg.asset || "—"} {pkg.duration_days != null ? `· ${pkg.duration_days}d` : ""}
                    </strong>
                    <div className="cr-muted">
                      {pkg.return_percentage != null ? `${pkg.return_percentage}% return` : ""}
                      {pkg.package_currency_fullname ? ` · ${pkg.package_currency_fullname}` : ""}
                    </div>
                  </td>
                  <td className="cr-num">{formatAmount(pkg.staking_amount)}</td>
                  <td>
                    <div>Bought {formatDate(pkg.purchase_date)}</div>
                    <div className="cr-muted">Matures {formatDate(pkg.maturity_date)}</div>
                  </td>
                  <td>
                    <Chip kind={stakingStatusKind(pkg.status)}>{pkg.status || "—"}</Chip>
                  </td>
                  <td className="cr-num">
                    {formatAmount(pkg.total_payouts_received)}
                    <div className="cr-muted">of {formatAmount(pkg.total_expected_return)}</div>
                  </td>
                  <td className="cr-num">
                    <strong>{formatAmount(gen.total)}</strong>
                    <div className="cr-muted">{gen.count ?? events.length} events</div>
                  </td>
                  {!compact ? (
                    <td>
                      {events.length ? (
                        <button type="button" className="cr-link-btn" onClick={() => onToggle(id)}>
                          {open ? "Hide payouts" : "Payout-wise"}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  ) : null}
                </tr>
                {!compact && open ? (
                  <tr>
                    <td colSpan={7} className="cr-nested-cell">
                      <table className="cr-table cr-table--nested">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Level</th>
                            <th>Upline received</th>
                            <th>Payout base</th>
                            <th>%</th>
                            <th>Commission</th>
                            <th>When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((ev) => (
                            <tr key={ev._id || ev.reference_transaction_id}>
                              <td>
                                <Chip kind={sourceChipKind(ev.commission_type)}>
                                  {sourceLabel(ev.commission_type)}
                                </Chip>
                              </td>
                              <td>
                                <Chip kind="level">L{ev.level ?? "—"}</Chip>
                              </td>
                              <td>
                                <PersonCell person={ev.upline} />
                              </td>
                              <td className="cr-num">
                                {formatAmount(ev.payout_amount)} {formatCurrencyLabel(ev.currency)}
                              </td>
                              <td>{formatPercent(ev.commission_percent)}</td>
                              <td className="cr-num">{formatAmount(ev.commission_amount)}</td>
                              <td>{formatDate(ev.credited_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TradesTable = ({ rows, expanded, onToggle, compact }) => {
  if (!rows.length) return <p className="cr-muted">No trades.</p>;
  return (
    <div className="cr-table-wrap">
      <table className="cr-table">
        <thead>
          <tr>
            <th>Pair</th>
            <th>Side</th>
            <th>Qty / price</th>
            <th>Total</th>
            <th>Fee</th>
            <th>Upline commission</th>
            <th>Date</th>
            {!compact ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => {
            const t = item?.trade || {};
            const gen = item?.commission_generated_for_uplines || {};
            const id = t._id || t.trade_id || `trade-${idx}`;
            const open = expanded.has(id);
            const details = Array.isArray(gen.details) ? gen.details : [];
            return (
              <React.Fragment key={id}>
                <tr>
                  <td>
                    <strong>{t.pair || "—"}</strong>
                    <div className="cr-muted">{t.order_type || ""}</div>
                  </td>
                  <td>
                    <Chip kind={String(t.side).toUpperCase() === "BUY" ? "buy" : "sell"}>{t.side || "—"}</Chip>
                  </td>
                  <td className="cr-num">
                    {formatAmount(t.quantity)}
                    <div className="cr-muted">@ {formatAmount(t.price)}</div>
                  </td>
                  <td className="cr-num">{formatAmount(t.total_amount)}</td>
                  <td className="cr-num">{formatAmount(t.brokerage_fee)}</td>
                  <td className="cr-num">
                    <strong>{formatAmount(gen.total)}</strong>
                    <div className="cr-muted">{gen.count ?? details.length} uplines</div>
                  </td>
                  <td>{formatDate(t.trade_date)}</td>
                  {!compact ? (
                    <td>
                      {details.length ? (
                        <button type="button" className="cr-link-btn" onClick={() => onToggle(id)}>
                          {open ? "Hide" : "Who got paid"}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  ) : null}
                </tr>
                {!compact && open ? (
                  <tr>
                    <td colSpan={8} className="cr-nested-cell">
                      <table className="cr-table cr-table--nested">
                        <thead>
                          <tr>
                            <th>Level</th>
                            <th>Upline</th>
                            <th>Trade fee</th>
                            <th>%</th>
                            <th>Commission</th>
                            <th>When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.map((ev) => (
                            <tr key={ev._id || ev.commission_transaction_id}>
                              <td>
                                <Chip kind="level">L{ev.level ?? "—"}</Chip>
                              </td>
                              <td>
                                <PersonCell person={ev.upline} />
                              </td>
                              <td className="cr-num">
                                {formatAmount(ev.trade_fee)} {formatCurrencyLabel(ev.currency)}
                              </td>
                              <td>{formatPercent(ev.commission_percentage)}</td>
                              <td className="cr-num">{formatAmount(ev.commission_amount)}</td>
                              <td>{formatDate(ev.credited_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const DownlineTxnTable = ({ rows }) => {
  if (!rows.length) return <p className="cr-muted">No downline commission credits.</p>;
  return (
    <div className="cr-table-wrap">
      <table className="cr-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Who generated it</th>
            <th>Level</th>
            <th>Activity</th>
            <th>Base</th>
            <th>%</th>
            <th>This user received</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const staking = String(row.commission_source || "").toUpperCase().includes("STAK");
            const pkg = row.package || {};
            return (
              <tr key={row._id}>
                <td>
                  <Chip kind={sourceChipKind(row.commission_source)}>{sourceLabel(row.commission_source)}</Chip>
                </td>
                <td>
                  <PersonCell person={row.source_user} />
                </td>
                <td>
                  <Chip kind="level">L{row.referral_level ?? "—"}</Chip>
                </td>
                <td>
                  {staking ? (
                    <>
                      <strong>
                        {pkg.package_currency || "—"} {pkg.duration_days != null ? `· ${pkg.duration_days}d` : ""}
                      </strong>
                      <div className="cr-muted">
                        Staked {formatAmount(pkg.staking_amount)} · {pkg.staking_status || ""}
                      </div>
                    </>
                  ) : (
                    <>
                      <strong>{row.pair || "—"}</strong>
                      <div className="cr-muted">Trade fee {formatAmount(row.trade_fee)}</div>
                    </>
                  )}
                </td>
                <td className="cr-num">
                  {staking ? formatAmount(row.payout_amount) : formatAmount(row.trade_fee)}
                </td>
                <td>{formatPercent(row.commission_percentage)}</td>
                <td className="cr-num">
                  <strong>{formatAmount(row.commission_amount)}</strong>
                  <div className="cr-muted">{formatCurrencyLabel(row.currency)}</div>
                </td>
                <td>{formatDate(row.commission_date)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const UplineTxnTable = ({ rows }) => {
  if (!rows.length) return <p className="cr-muted">No commission paid to uplines.</p>;
  return (
    <div className="cr-table-wrap">
      <table className="cr-table">
        <thead>
          <tr>
            <th>Upline received</th>
            <th>Level</th>
            <th>From</th>
            <th>Activity</th>
            <th>Base</th>
            <th>%</th>
            <th>Paid</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const staking = String(row.commission_source || "").toUpperCase().includes("STAK");
            const pkg = row.package || {};
            return (
              <tr key={row._id}>
                <td>
                  <PersonCell person={row.upline} />
                </td>
                <td>
                  <Chip kind="level">L{row.upline_level ?? "—"}</Chip>
                </td>
                <td>
                  <Chip kind={sourceChipKind(row.commission_source)}>{sourceLabel(row.commission_source)}</Chip>
                </td>
                <td>
                  {staking ? (
                    <>
                      <strong>
                        {pkg.package_currency || "—"} {pkg.duration_days != null ? `· ${pkg.duration_days}d` : ""}
                      </strong>
                      <div className="cr-muted">
                        Staked {formatAmount(pkg.staking_amount)} · {pkg.staking_status || ""}
                      </div>
                    </>
                  ) : (
                    <>
                      <strong>{row.pair || "—"}</strong>
                      <div className="cr-muted">Trade {row.trade_id || ""}</div>
                    </>
                  )}
                </td>
                <td className="cr-num">{formatAmount(row.source_amount)}</td>
                <td>{formatPercent(row.commission_percentage)}</td>
                <td className="cr-num">
                  <strong>{formatAmount(row.commission_amount)}</strong>
                  <div className="cr-muted">
                    {formatCurrencyLabel(row.currency)} · {row.transaction_status || "CREDITED"}
                  </div>
                </td>
                <td>{formatDate(row.commission_date)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const LevelMembers = ({ levels }) => {
  const list = Array.isArray(levels) ? levels : [];
  if (!list.length) return <p className="cr-muted">No downline members.</p>;
  return (
    <div className="cr-level-grid">
      {[1, 2, 3, 4, 5].map((lvl) => {
        const block = list.find((x) => Number(x.level) === lvl) || { level: lvl, count: 0, members: [] };
        const members = Array.isArray(block.members) ? block.members : [];
        return (
          <div key={lvl} className="cr-level-card">
            <span className="cr-level-card__lvl">Level {lvl}</span>
            <span className="cr-level-card__count">{block.count ?? members.length}</span>
            <span className="cr-muted">{lvl === 1 ? "Direct referrals" : `Indirect L${lvl}`}</span>
            {members.length ? (
              <ul>
                {members.slice(0, 4).map((m) => (
                  <li key={m._id}>
                    <strong>{personName(m)}</strong>
                    <div>{m.uuid || m.email || ""}</div>
                  </li>
                ))}
                {members.length > 4 ? <li>+{members.length - 4} more</li> : null}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const CommissionReportDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "overview";
  const tab = TAB_IDS.includes(tabParam) ? tabParam : "overview";

  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const [report, setReport] = useState(null);

  const [staking, setStaking] = useState({ rows: [], pagination: EMPTY_PAGINATION, loading: false, error: "" });
  const [trades, setTrades] = useState({ rows: [], pagination: EMPTY_PAGINATION, loading: false, error: "" });
  const [downline, setDownline] = useState({
    members: [],
    rows: [],
    pagination: EMPTY_PAGINATION,
    loading: false,
    error: "",
  });
  const [upline, setUpline] = useState({ rows: [], pagination: EMPTY_PAGINATION, loading: false, error: "" });

  const [stakingFrom, setStakingFrom] = useState("");
  const [stakingTo, setStakingTo] = useState("");
  const [tradesFrom, setTradesFrom] = useState("");
  const [tradesTo, setTradesTo] = useState("");
  const [downFrom, setDownFrom] = useState("");
  const [downTo, setDownTo] = useState("");
  const [downType, setDownType] = useState("");
  const [upFrom, setUpFrom] = useState("");
  const [upTo, setUpTo] = useState("");
  const [upType, setUpType] = useState("");

  const [expandedStake, setExpandedStake] = useState(() => new Set());
  const [expandedTrade, setExpandedTrade] = useState(() => new Set());

  const setTab = (id) => {
    if (id === "overview") setSearchParams({});
    else setSearchParams({ tab: id });
  };

  const toggleSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchOverview = useCallback(async () => {
    if (!userId) return;
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const res = await AdminService.getUserCommissionReport(userId);
      if (res?.success) {
        setReport(res.data || null);
      } else {
        setReport(null);
        setOverviewError(res?.message || "Failed to fetch user commission report.");
      }
    } catch (err) {
      setReport(null);
      setOverviewError(err?.response?.data?.message || err?.message || "Failed to fetch user commission report.");
    } finally {
      setOverviewLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const fetchStaking = useCallback(
    async (page = 1, limit = staking.pagination.limit, from = stakingFrom, to = stakingTo) => {
      if (!userId) return;
      setStaking((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const params = { page, limit };
        const fromIso = dateToFromIso(from);
        const toIso = dateToToIso(to);
        if (fromIso) params.from = fromIso;
        if (toIso) params.to = toIso;
        const res = await AdminService.getUserCommissionStakingDetails(userId, params);
        if (res?.success) {
          setStaking({
            rows: Array.isArray(res.data) ? res.data : [],
            pagination: { ...EMPTY_PAGINATION, ...(res.pagination || {}), page: res.pagination?.page || page, limit: res.pagination?.limit ?? limit },
            loading: false,
            error: "",
          });
        } else {
          setStaking({ rows: [], pagination: EMPTY_PAGINATION, loading: false, error: res?.message || "Failed to fetch staking details." });
        }
      } catch (err) {
        setStaking({
          rows: [],
          pagination: EMPTY_PAGINATION,
          loading: false,
          error: err?.response?.data?.message || err?.message || "Failed to fetch staking details.",
        });
      }
    },
    [userId, staking.pagination.limit, stakingFrom, stakingTo]
  );

  const fetchTrades = useCallback(
    async (page = 1, limit = trades.pagination.limit, from = tradesFrom, to = tradesTo) => {
      if (!userId) return;
      setTrades((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const params = { page, limit };
        const fromIso = dateToFromIso(from);
        const toIso = dateToToIso(to);
        if (fromIso) params.from = fromIso;
        if (toIso) params.to = toIso;
        const res = await AdminService.getUserCommissionTradingDetails(userId, params);
        if (res?.success) {
          setTrades({
            rows: Array.isArray(res.data) ? res.data : [],
            pagination: { ...EMPTY_PAGINATION, ...(res.pagination || {}), page: res.pagination?.page || page, limit: res.pagination?.limit ?? limit },
            loading: false,
            error: "",
          });
        } else {
          setTrades({ rows: [], pagination: EMPTY_PAGINATION, loading: false, error: res?.message || "Failed to fetch trading details." });
        }
      } catch (err) {
        setTrades({
          rows: [],
          pagination: EMPTY_PAGINATION,
          loading: false,
          error: err?.response?.data?.message || err?.message || "Failed to fetch trading details.",
        });
      }
    },
    [userId, trades.pagination.limit, tradesFrom, tradesTo]
  );

  const fetchDownline = useCallback(
    async (page = 1, limit = downline.pagination.limit, from = downFrom, to = downTo, type = downType) => {
      if (!userId) return;
      setDownline((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const params = { page, limit };
        const fromIso = dateToFromIso(from);
        const toIso = dateToToIso(to);
        if (fromIso) params.from = fromIso;
        if (toIso) params.to = toIso;
        if (type) params.type = type;
        const res = await AdminService.getUserCommissionDownline(userId, params);
        if (res?.success) {
          const data = res.data || {};
          setDownline({
            members: Array.isArray(data.downline_members) ? data.downline_members : [],
            rows: Array.isArray(data.commission_transactions) ? data.commission_transactions : [],
            pagination: { ...EMPTY_PAGINATION, ...(res.pagination || {}), page: res.pagination?.page || page, limit: res.pagination?.limit ?? limit },
            loading: false,
            error: "",
          });
        } else {
          setDownline({ members: [], rows: [], pagination: EMPTY_PAGINATION, loading: false, error: res?.message || "Failed to fetch downline commission." });
        }
      } catch (err) {
        setDownline({
          members: [],
          rows: [],
          pagination: EMPTY_PAGINATION,
          loading: false,
          error: err?.response?.data?.message || err?.message || "Failed to fetch downline commission.",
        });
      }
    },
    [userId, downline.pagination.limit, downFrom, downTo, downType]
  );

  const fetchUpline = useCallback(
    async (page = 1, limit = upline.pagination.limit, from = upFrom, to = upTo, type = upType) => {
      if (!userId) return;
      setUpline((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const params = { page, limit };
        const fromIso = dateToFromIso(from);
        const toIso = dateToToIso(to);
        if (fromIso) params.from = fromIso;
        if (toIso) params.to = toIso;
        if (type) params.type = type;
        const res = await AdminService.getUserCommissionUpline(userId, params);
        if (res?.success) {
          setUpline({
            rows: Array.isArray(res.data) ? res.data : [],
            pagination: { ...EMPTY_PAGINATION, ...(res.pagination || {}), page: res.pagination?.page || page, limit: res.pagination?.limit ?? limit },
            loading: false,
            error: "",
          });
        } else {
          setUpline({ rows: [], pagination: EMPTY_PAGINATION, loading: false, error: res?.message || "Failed to fetch upline commission." });
        }
      } catch (err) {
        setUpline({
          rows: [],
          pagination: EMPTY_PAGINATION,
          loading: false,
          error: err?.response?.data?.message || err?.message || "Failed to fetch upline commission.",
        });
      }
    },
    [userId, upline.pagination.limit, upFrom, upTo, upType]
  );

  useEffect(() => {
    if (tab === "staking") fetchStaking(1);
    if (tab === "trades") fetchTrades(1);
    if (tab === "downline") fetchDownline(1);
    if (tab === "upline") fetchUpline(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when tab changes
  }, [tab, userId]);

  const user = report?.user_info || {};
  const summary = report?.summary || {};
  const sponsor = user.sponsor;

  const overviewCharts = useMemo(() => {
    const s = report?.summary || {};
    const sourceSlices = [
      { label: "Staking earned", value: asNum(s.total_staking_commission_earned), color: COLORS.staking },
      { label: "Trading earned", value: asNum(s.total_brokerage_commission_earned), color: COLORS.brokerage },
    ];
    const networkSlices = [
      { label: "Direct (L1)", value: asNum(s.total_direct_referral_commission_earned), color: COLORS.direct },
      { label: "Deeper downline (L2–5)", value: asNum(s.total_downline_commission_earned), color: COLORS.downline },
    ];
    const generatedSlices = [
      { label: "From staking", value: asNum(s.total_commission_generated_through_staking), color: COLORS.staking },
      { label: "From trading", value: asNum(s.total_commission_generated_through_trading), color: COLORS.brokerage },
    ];
    const levels = report?.direct_referrals_and_downline?.levels_preview || [];
    const levelBars = [1, 2, 3, 4, 5].map((lvl, idx) => {
      const block = levels.find((x) => Number(x.level) === lvl);
      return {
        label: `L${lvl}`,
        value: asNum(block?.count),
        color: COLORS.level[idx],
      };
    });
    return { sourceSlices, networkSlices, generatedSlices, levelBars };
  }, [report]);

  const tabCounts = {
    staking: report?.staking_packages?.total_count,
    trades: report?.trades?.total_count,
    downline: report?.downline_commission?.total_count,
    upline: report?.upline_commission_given?.total_count,
  };

  return (
    <main className="dashboard-main dashboard-subpage cr-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/commission-report">Commission Report</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>{personName(user) === "—" ? "User" : personName(user)}</span>
          </p>
          <h1 className="dashboard-hero__title">User Commission Report</h1>
          <p className="dashboard-hero__subtitle">
            {user.email || "—"} {user.uuid ? `· ${user.uuid}` : ""}
          </p>
        </div>
        <div className="dashboard-hero__icon cr-hero__icon">
          <i className="fas fa-user-circle" />
        </div>
      </section>

      <div className="dashboard-body dashboard-content-inner">
        <div className="cr-detail-head">
          <button type="button" className="cr-btn-ghost" onClick={() => navigate("/dashboard/commission-report")}>
            <i className="fas fa-arrow-left" /> Back to list
          </button>
        </div>

        {overviewLoading ? (
          <EmptyBlock loading />
        ) : overviewError ? (
          <EmptyBlock error={overviewError} />
        ) : (
          <>
            <div className="cr-detail-meta">
              <Chip kind={accountClass(user.account_status)}>{user.account_status || "—"}</Chip>
              <span>
                Joined <strong>{formatDate(user.registration_date)}</strong>
              </span>
              <span>
                Direct referrals <strong>{user.total_direct_referrals ?? 0}</strong>
              </span>
              <span>
                Full downline <strong>{user.total_downline_members ?? 0}</strong>
              </span>
              <span>
                Mobile <strong>{user.mobile || "—"}</strong>
              </span>
              {sponsor ? (
                <span>
                  Sponsor <strong>{personName(sponsor)}</strong> ({sponsor.uuid || sponsor.email || "—"})
                </span>
              ) : (
                <span>Sponsor <strong>None</strong></span>
              )}
            </div>

            <div className="cr-tabs" role="tablist">
              {[
                { id: "overview", label: "Overview", icon: "fa-chart-pie" },
                { id: "staking", label: "Staking Packages", icon: "fa-layer-group", count: tabCounts.staking },
                { id: "trades", label: "Trades", icon: "fa-exchange-alt", count: tabCounts.trades },
                { id: "downline", label: "Downline Commission", icon: "fa-level-down-alt", count: tabCounts.downline },
                { id: "upline", label: "Upline Commission", icon: "fa-level-up-alt", count: tabCounts.upline },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={`cr-tab ${tab === t.id ? "cr-tab--active" : ""}`}
                  onClick={() => setTab(t.id)}
                >
                  <i className={`fas ${t.icon}`} />
                  {t.label}
                  {t.count != null ? <span className="cr-tab__count">{t.count}</span> : null}
                </button>
              ))}
            </div>

            {tab === "overview" ? (
              <>
                <p className="cr-help">
                  Money <strong>in</strong> is commission this user earned from their network’s staking and trades.
                  Money <strong>out</strong> is commission this user’s own activity paid to their uplines.
                </p>

                <div className="cr-flow">
                  <div className="cr-flow__box">
                    <h3>Earned from downline</h3>
                    <strong>{formatAmount(summary.total_commission_earned)}</strong>
                    <p>
                      Staking {formatAmount(summary.total_staking_commission_earned)} · Trading{" "}
                      {formatAmount(summary.total_brokerage_commission_earned)}
                    </p>
                  </div>
                  <div className="cr-flow__arrow" aria-hidden>
                    <i className="fas fa-arrows-alt-h" />
                  </div>
                  <div className="cr-flow__box">
                    <h3>Generated for uplines</h3>
                    <strong>{formatAmount(summary.total_commission_generated_for_uplines)}</strong>
                    <p>
                      From staking {formatAmount(summary.total_commission_generated_through_staking)} · From trading{" "}
                      {formatAmount(summary.total_commission_generated_through_trading)}
                    </p>
                  </div>
                </div>

                <div className="cr-stat-grid">
                  <div>
                    <span>Direct (L1) earned</span>
                    <strong>{formatAmount(summary.total_direct_referral_commission_earned)}</strong>
                    <small>From people they referred</small>
                  </div>
                  <div>
                    <span>Deeper downline earned</span>
                    <strong>{formatAmount(summary.total_downline_commission_earned)}</strong>
                    <small>Levels 2–5</small>
                  </div>
                  <div>
                    <span>Packages purchased</span>
                    <strong>{summary.total_packages_purchased ?? 0}</strong>
                    <small>Staked {formatAmount(summary.total_staking_amount)}</small>
                  </div>
                  <div>
                    <span>Trades</span>
                    <strong>{summary.total_trades ?? 0}</strong>
                    <small>Fees paid {formatAmount(summary.total_brokerage_paid)}</small>
                  </div>
                </div>

                <div className="cr-charts">
                  <article className="cr-chart-card">
                    <h3>Earned by source</h3>
                    <div className="cr-chart-card__body">
                      <DonutChart
                        slices={overviewCharts.sourceSlices}
                        centerLabel={formatAmount(summary.total_commission_earned)}
                        centerSub="Total earned"
                      />
                      <ChartLegend items={overviewCharts.sourceSlices} />
                    </div>
                  </article>
                  <article className="cr-chart-card">
                    <h3>Direct vs deeper network</h3>
                    <div className="cr-chart-card__body">
                      <DonutChart
                        slices={overviewCharts.networkSlices}
                        centerLabel={formatAmount(
                          asNum(summary.total_direct_referral_commission_earned) +
                            asNum(summary.total_downline_commission_earned)
                        )}
                        centerSub="Earned"
                      />
                      <ChartLegend items={overviewCharts.networkSlices} />
                    </div>
                  </article>
                  <article className="cr-chart-card">
                    <h3>What this user paid upward</h3>
                    <div className="cr-chart-card__body">
                      <DonutChart
                        slices={overviewCharts.generatedSlices}
                        centerLabel={formatAmount(summary.total_commission_generated_for_uplines)}
                        centerSub="To uplines"
                      />
                      <ChartLegend items={overviewCharts.generatedSlices} />
                    </div>
                  </article>
                </div>

                <section className="cr-card">
                  <h3 style={{ marginTop: 0 }}>Downline by level</h3>
                  <BarList items={overviewCharts.levelBars} />
                  <div style={{ marginTop: "0.85rem" }}>
                    <LevelMembers levels={report?.direct_referrals_and_downline?.levels_preview} />
                  </div>
                </section>

                <section className="cr-card">
                  <PreviewHead
                    title="Staking packages"
                    count={report?.staking_packages?.total_count}
                    onViewAll={() => setTab("staking")}
                  />
                  <StakingTable
                    rows={Array.isArray(report?.staking_packages?.preview) ? report.staking_packages.preview : []}
                    expanded={new Set()}
                    onToggle={() => {}}
                    compact
                  />
                </section>

                <section className="cr-card">
                  <PreviewHead
                    title="Trades"
                    count={report?.trades?.total_count}
                    onViewAll={() => setTab("trades")}
                  />
                  <TradesTable
                    rows={Array.isArray(report?.trades?.preview) ? report.trades.preview : []}
                    expanded={new Set()}
                    onToggle={() => {}}
                    compact
                  />
                </section>

                <section className="cr-card">
                  <PreviewHead
                    title="Downline commission (money in)"
                    count={report?.downline_commission?.total_count}
                    onViewAll={() => setTab("downline")}
                  />
                  <DownlineTxnTable
                    rows={
                      Array.isArray(report?.downline_commission?.preview) ? report.downline_commission.preview : []
                    }
                  />
                </section>

                <section className="cr-card">
                  <PreviewHead
                    title="Upline commission (money out)"
                    count={report?.upline_commission_given?.total_count}
                    onViewAll={() => setTab("upline")}
                  />
                  <UplineTxnTable
                    rows={
                      Array.isArray(report?.upline_commission_given?.preview)
                        ? report.upline_commission_given.preview
                        : []
                    }
                  />
                </section>
              </>
            ) : null}

            {tab === "staking" ? (
              <section className="cr-card">
                <p className="cr-help">
                  Each package this user bought, plus every commission those payouts generated for their uplines.
                </p>
                <DateTypeFilters
                  fromDate={stakingFrom}
                  toDate={stakingTo}
                  onFrom={setStakingFrom}
                  onTo={setStakingTo}
                  onApply={() => fetchStaking(1, staking.pagination.limit, stakingFrom, stakingTo)}
                  onReset={() => {
                    setStakingFrom("");
                    setStakingTo("");
                    fetchStaking(1, staking.pagination.limit, "", "");
                  }}
                />
                {staking.loading || staking.error ? (
                  <EmptyBlock loading={staking.loading} error={staking.error} />
                ) : (
                  <>
                    <StakingTable
                      rows={staking.rows}
                      expanded={expandedStake}
                      onToggle={(id) => toggleSet(setExpandedStake, id)}
                    />
                    <PaginationBar
                      pagination={staking.pagination}
                      loading={staking.loading}
                      onPageChange={(p) => fetchStaking(p)}
                      onLimitChange={(n) => fetchStaking(1, n)}
                    />
                  </>
                )}
              </section>
            ) : null}

            {tab === "trades" ? (
              <section className="cr-card">
                <p className="cr-help">
                  Each trade this user made, and how much of the fee was shared with each upline.
                </p>
                <DateTypeFilters
                  fromDate={tradesFrom}
                  toDate={tradesTo}
                  onFrom={setTradesFrom}
                  onTo={setTradesTo}
                  onApply={() => fetchTrades(1, trades.pagination.limit, tradesFrom, tradesTo)}
                  onReset={() => {
                    setTradesFrom("");
                    setTradesTo("");
                    fetchTrades(1, trades.pagination.limit, "", "");
                  }}
                />
                {trades.loading || trades.error ? (
                  <EmptyBlock loading={trades.loading} error={trades.error} />
                ) : (
                  <>
                    <TradesTable
                      rows={trades.rows}
                      expanded={expandedTrade}
                      onToggle={(id) => toggleSet(setExpandedTrade, id)}
                    />
                    <PaginationBar
                      pagination={trades.pagination}
                      loading={trades.loading}
                      onPageChange={(p) => fetchTrades(p)}
                      onLimitChange={(n) => fetchTrades(1, n)}
                    />
                  </>
                )}
              </section>
            ) : null}

            {tab === "downline" ? (
              <section className="cr-card">
                <p className="cr-help">
                  Money flowing <strong>into</strong> this user: which downline staked or traded, and how much commission that created here.
                </p>
                <DateTypeFilters
                  fromDate={downFrom}
                  toDate={downTo}
                  typeValue={downType}
                  onFrom={setDownFrom}
                  onTo={setDownTo}
                  onType={setDownType}
                  showType
                  onApply={() => fetchDownline(1, downline.pagination.limit, downFrom, downTo, downType)}
                  onReset={() => {
                    setDownFrom("");
                    setDownTo("");
                    setDownType("");
                    fetchDownline(1, downline.pagination.limit, "", "", "");
                  }}
                />
                {downline.loading || downline.error ? (
                  <EmptyBlock loading={downline.loading} error={downline.error} />
                ) : (
                  <>
                    <h3 style={{ marginTop: 0 }}>Referral tree</h3>
                    <LevelMembers levels={downline.members} />
                    <h3>Commission credits</h3>
                    <DownlineTxnTable rows={downline.rows} />
                    <PaginationBar
                      pagination={downline.pagination}
                      loading={downline.loading}
                      onPageChange={(p) => fetchDownline(p)}
                      onLimitChange={(n) => fetchDownline(1, n)}
                    />
                  </>
                )}
              </section>
            ) : null}

            {tab === "upline" ? (
              <section className="cr-card">
                <p className="cr-help">
                  Money flowing <strong>upward</strong> from this user’s own staking and trades — who above them was paid, and from what.
                </p>
                <DateTypeFilters
                  fromDate={upFrom}
                  toDate={upTo}
                  typeValue={upType}
                  onFrom={setUpFrom}
                  onTo={setUpTo}
                  onType={setUpType}
                  showType
                  onApply={() => fetchUpline(1, upline.pagination.limit, upFrom, upTo, upType)}
                  onReset={() => {
                    setUpFrom("");
                    setUpTo("");
                    setUpType("");
                    fetchUpline(1, upline.pagination.limit, "", "", "");
                  }}
                />
                {upline.loading || upline.error ? (
                  <EmptyBlock loading={upline.loading} error={upline.error} />
                ) : (
                  <>
                    <UplineTxnTable rows={upline.rows} />
                    <PaginationBar
                      pagination={upline.pagination}
                      loading={upline.loading}
                      onPageChange={(p) => fetchUpline(p)}
                      onLimitChange={(n) => fetchUpline(1, n)}
                    />
                  </>
                )}
              </section>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
};

export default CommissionReportDetailPage;
